import Component from '@glimmer/component';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { task, rawTimeout, timeout } from 'ember-concurrency';
import noUiSlider from 'nouislider';
import { add } from 'date-fns';
import { registerDestructor } from '@ember/destroyable';

export default class PositionControlComponent extends Component {
  @service stereo;
  @service playback;
  @service session;
  @service fetch;
  @service intl;
  @service clock;

  @tracked isDestroying = false;

  constructor() {
    super(...arguments);
    registerDestructor(this, () => {
      this.isDestroying = true;
      if (this.slider) {
        this.slider.off('start');
        this.slider.off('slide');
        this.slider.off('drag');
        this.slider.off('update');
        this.slider.off('change');
        this.slider.off('set');
        this.slider.off('end');
        this.slider.destroy();
      }
    });
  }

  get startsAt() {
    return (
      this.args.feed?.airing?.actualStartsAt || this.args.feed?.airing?.startsAt
    );
  }

  positionToTime = (position) => {
    if (this.startsAt && position) {
      return add(new Date(this.startsAt), {
        seconds: (position || 0) / 1000,
      });
    } else if (this.startsAt) {
      return this.startsAt;
    } else {
      return undefined;
    }
  };

  initializeSliderTask = task(async () => {
    let min = 0;
    while (!this.loadedSound || this.loadedSound?.isStream) {
      await rawTimeout(10);
    }

    let max = this.loadedSound.duration;

    let range = {
      min: [min, 1000],
      max: [max, 1000],
    };

    this.slider = noUiSlider.create(this.sliderElement, {
      connect: [true, false],
      padding: [10],
      keyboardSupport: true,
      keyboardDefaultStep: 1000,
      tooltips: [
        {
          to: (value) =>
            this.intl.formatTime(this.positionToTime(value), {
              hour: 'numeric',
              minute: 'numeric',
              second: 'numeric',
              timeZone: this.args.timeZone,
              locale: this.args.locale
            }),
        },
      ],
      range: range,
      padding: [10],
      start: [this.loadedSound.position],
      cssPrefix: 'dubbletrack-slider-',
    });

    this.slider.enable();
    this.slider.on('change', this.onSliderUpdate.bind(this));
    this.slider.on('slide', this.onSliderUpdate.bind(this));
    this.slider.on('drag', this.onSliderUpdate.bind(this));
    this.updatePlayheadTask.perform();
  });

  onSliderUpdate(values, handle) {
    let position = values[handle];
    this.lastUpdate = new Date();

    if (position && this.loadedSound) {
      this.loadedSound.position = position;
    }

    let payload = {
      handle: handle,
      time: this.positionToTime(values[handle]),
      position: values[handle],
    };

    if ('onUpdate' in this.args) {
      this.args.onUpdate(handle, payload);
    }
    if ('onUpdateStart' in this.args && handle == 0) {
      this.args.onUpdateStart(payload);
    }
    if ('onUpdateEnd' in this.args && handle == 1) {
      this.args.onUpdateEnd(payload);
    }
  }

  updatePlayheadTask = task(async () => {
    while (true) {
      if (this.loadedSound && this.loadedSound.isPlaying) {
        this.slider.setHandle(0, this.loadedSound.position, false);
      }
      await timeout(1000);
    }
  });

  get loadedSound() {
    return this.args.feed?.loadedSound;
  }

  @action
  async didInsert(element) {
    this.sliderElement = element;
    if (this.slider) {
      this.slider.destroy();
    }
    this.initializeSliderTask.perform();
  }
}
